// Store Inventory with the kitchen flow on (batch 4, lane store): five tabs —
// To do | Stock | Buying | Count | More — instead of eleven. The old tab ids
// stay in the URL, so every `?tab=` link still lands where it did; the group
// is derived from it (storeScreenLogic.groupOfTab).
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { InventoryTab } from '@/components/store/inventoryRouteSearch'
import { GROUP_HOME, STORE_GROUPS, groupOfTab, moreTabs, type StoreGroup } from '@/lib/inventoryFlow/storeScreenLogic'
import { ST } from '@/lib/inventoryFlow/storeScreenStrings'

const segment = 'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors'
const on = 'bg-background text-foreground shadow-sm'
const off = 'text-muted-foreground hover:text-foreground'

export function StoreTabBar({
  tab,
  onTab,
  isAdmin,
  todoBadge,
  moreLabels,
  moreBadges,
}: {
  tab: InventoryTab
  onTab: (t: InventoryTab) => void
  isAdmin: boolean
  todoBadge: number
  /** Labels for the tabs under More (some live in their own strings files). */
  moreLabels: Partial<Record<InventoryTab, string>>
  moreBadges?: Partial<Record<InventoryTab, number>>
}) {
  const group = groupOfTab(tab)
  const more = moreTabs(isAdmin)
  const moreBadge = more.reduce((s, t) => s + (moreBadges?.[t] ?? 0), 0)
  return (
    <div className="space-y-3">
      <div role="tablist" aria-label="Store" className="flex w-fit max-w-full flex-wrap gap-1 rounded-lg bg-muted/60 p-1">
        {STORE_GROUPS.map((g: StoreGroup) => {
          if (g === 'more') {
            return (
              <DropdownMenu key={g}>
                <DropdownMenuTrigger asChild>
                  <button type="button" role="tab" aria-selected={group === 'more'} className={cn(segment, group === 'more' ? on : off)}>
                    {group === 'more' ? `${ST.tabs.more} · ${moreLabels[tab] ?? ''}` : ST.tabs.more}
                    {group !== 'more' && moreBadge > 0 && <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums text-muted-foreground">{moreBadge}</span>}
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-52">
                  {more.map((t) => (
                    <DropdownMenuItem key={t} onClick={() => onTab(t)} className={cn(tab === t && 'font-semibold')}>
                      <span className="flex-1">{moreLabels[t] ?? t}</span>
                      {(moreBadges?.[t] ?? 0) > 0 && <span className="text-xs tabular-nums text-muted-foreground">{moreBadges?.[t]}</span>}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )
          }
          return (
            <button key={g} type="button" role="tab" aria-selected={group === g} onClick={() => onTab(GROUP_HOME[g])} className={cn(segment, group === g ? on : off)}>
              {ST.tabs[g]}
              {g === 'todo' && todoBadge > 0 && <Badge variant="destructive" className="px-1.5 py-0 text-xs">{todoBadge}</Badge>}
            </button>
          )
        })}
      </div>
      {group === 'buying' && (
        <div className="flex gap-4 border-b text-sm">
          {(['purchasing', 'vendors'] as const).map((t) => (
            <button key={t} type="button" onClick={() => onTab(t)}
              className={cn('-mb-px border-b-2 px-1 pb-2 font-medium transition-colors', tab === t ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}>
              {ST.buying[t]}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/** The quiet note in place of hundreds of "low stock" warnings for items no
 *  one has set levels for yet. */
export function NoLevelsNote({ count, onSetLevels }: { count: number; onSetLevels: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/30 px-4 py-2.5 text-sm">
      <p className="min-w-0 flex-1 text-muted-foreground">
        <span className="font-semibold text-foreground">{ST.noLevels(count)}</span> {ST.noLevelsRest}
      </p>
      <button type="button" onClick={onSetLevels} className="font-medium underline-offset-4 hover:underline">
        {ST.setLevels}
      </button>
    </div>
  )
}
